/** Documentos associados aos elementos.
 *
 *  O IFC sabe dizer que uma parede tem uma ficha técnica, que um
 *  equipamento tem um manual, que um lote tem um certificado. Está lá,
 *  em `IfcRelAssociatesDocument`, e nós não líamos nada disso: quem
 *  abrisse um modelo bem preparado não via os documentos que o autor
 *  teve o trabalho de associar.
 *
 *  Não guardamos o ficheiro nem o vamos buscar. O IFC não traz o PDF
 *  lá dentro: traz uma referência, que é quase sempre um caminho ou um
 *  endereço. O que fazemos é mostrar o que o modelo diz, incluindo
 *  quando o que ele diz é um caminho numa máquina que não é a nossa.
 *  Vale mais ver "\\servidor\projecto\fichas\parede-ext.pdf" e saber
 *  onde procurar do que não ver nada.
 *
 *  Dois esquemas, duas formas. O IFC2x3 e o IFC4 escrevem isto com
 *  nomes de atributo diferentes, e no 2x3 a `IfcDocumentInformation`
 *  nem sequer tem sítio para a localização. Por isso a leitura aceita
 *  os dois nomes em cada campo, em vez de haver dois caminhos de
 *  código.
 */

import { rawIfc } from '@viewer/lib/rawIfc'

/** Um documento como o modelo o declara. Tudo pode faltar: um IFC mal
 *  preenchido traz uma referência com nome e mais nada. */
export interface Documento {
  /** O código do documento: `Identification` no IFC4,
   *  `ItemReference` ou `DocumentId` no IFC2x3. */
  identificacao: string | null
  nome: string | null
  descricao: string | null
  /** Onde está. Um URL, um caminho de rede, um código de arquivo. */
  localizacao: string | null
  revisao: string | null
  /** `Status` da IfcDocumentInformation, quando existe. */
  estado: string | null
  /** O nome do type de onde o documento vem, quando não está
   *  associado ao elemento mas ao seu IfcXxxType. Vale para todas as
   *  instâncias desse type. */
  viaTipo?: string | null
}

/** Uma relação instância-type, já reduzida ao que interessa. */
export interface RelacaoDeTipo {
  tipo: number
  nomeDoTipo: string | null
  objectos: number[]
}

/**
 * Herdar os documentos do type.
 *
 * Se o autor associou a ficha técnica ao IfcWallType em vez de a cada
 * parede, a parede tem de a mostrar na mesma: é a forma mais comum de
 * documentar um produto, uma vez por tipo. Os documentos herdados vêm
 * marcados com o nome do type, para se saber de onde vêm, e não se
 * repetem se o elemento já os tiver por si.
 */
export function herdarDoTipo(
  indice: Map<number, Documento[]>,
  relacoes: readonly RelacaoDeTipo[],
): Map<number, Documento[]> {
  for (const r of relacoes) {
    const doTipo = indice.get(r.tipo)
    if (!doTipo || doTipo.length === 0) continue
    for (const o of r.objectos) {
      const herdados = doTipo.map((d) => ({ ...d, viaTipo: r.nomeDoTipo ?? '(type sem nome)' }))
      indice.set(o, semRepetidos([...(indice.get(o) ?? []), ...herdados]))
    }
  }
  return indice
}

function desembrulhar(v: unknown): unknown {
  if (v && typeof v === 'object' && 'value' in (v as Record<string, unknown>)) {
    return (v as { value: unknown }).value
  }
  return v
}

/** Lê um campo de texto, e devolve null quando não há texto nenhum.
 *
 *  Uma cadeia vazia não é um valor: mostrada na interface faz uma
 *  linha em branco que parece um defeito nosso. */
export function texto(v: unknown): string | null {
  const u = desembrulhar(v)
  if (u === null || u === undefined) return null
  const s = String(u).trim()
  return s.length === 0 ? null : s
}

/** O primeiro dos nomes alternativos que traga alguma coisa. */
function primeiro(o: Record<string, unknown>, ...nomes: string[]): string | null {
  for (const n of nomes) {
    const t = texto(o[n])
    if (t !== null) return t
  }
  return null
}

/**
 * Lê uma `IfcDocumentReference`.
 *
 * IFC4: (Location, Identification, Name, Description, ReferencedDocument)
 * IFC2x3: (Location, ItemReference, Name)
 */
export function lerReferencia(o: Record<string, unknown>): Documento {
  return {
    identificacao: primeiro(o, 'Identification', 'ItemReference'),
    nome: primeiro(o, 'Name'),
    descricao: primeiro(o, 'Description'),
    localizacao: primeiro(o, 'Location'),
    revisao: null,
    estado: null,
  }
}

/**
 * Lê uma `IfcDocumentInformation`.
 *
 * IFC4: (Identification, Name, Description, Location, Purpose, …, Revision, …, Status)
 * IFC2x3: (DocumentId, Name, Description, DocumentReferences, Purpose, …, Revision, …)
 *
 * No 2x3 não há `Location` nesta entidade: a localização vive na
 * referência. Daí a junção mais abaixo.
 */
export function lerInformacao(o: Record<string, unknown>): Documento {
  return {
    identificacao: primeiro(o, 'Identification', 'DocumentId'),
    nome: primeiro(o, 'Name'),
    descricao: primeiro(o, 'Description'),
    localizacao: primeiro(o, 'Location'),
    revisao: primeiro(o, 'Revision'),
    estado: primeiro(o, 'Status'),
  }
}

/**
 * Junta o que a referência diz com o que o documento referido diz.
 *
 * Manda a referência, quando tem valor. É ela que está pendurada no
 * elemento, e é ela que diz qual das páginas do documento interessa;
 * o documento serve para preencher o que ela não disse.
 */
export function juntar(
  referencia: Documento,
  informacao: Documento | null,
): Documento {
  if (!informacao) return referencia
  return {
    identificacao: referencia.identificacao ?? informacao.identificacao,
    nome: referencia.nome ?? informacao.nome,
    descricao: referencia.descricao ?? informacao.descricao,
    localizacao: referencia.localizacao ?? informacao.localizacao,
    revisao: referencia.revisao ?? informacao.revisao,
    estado: referencia.estado ?? informacao.estado,
  }
}

/** Há aqui alguma coisa que valha a pena mostrar? */
export function temConteudo(d: Documento): boolean {
  return (
    d.identificacao !== null ||
    d.nome !== null ||
    d.descricao !== null ||
    d.localizacao !== null
  )
}

/** Como chamar ao documento numa lista. Nunca devolve vazio: um item
 *  sem etiqueta é um item que ninguém consegue clicar com confiança. */
export function etiqueta(d: Documento): string {
  if (d.nome && d.identificacao) return `${d.identificacao} · ${d.nome}`
  return d.nome ?? d.identificacao ?? d.localizacao ?? 'Documento sem nome'
}

/**
 * A localização é um endereço que o browser consegue abrir?
 *
 * Só `http` e `https`. Um `file://` ou um caminho de rede não abrem a
 * partir de uma página, por desenho do browser e ainda bem; mostrá-los
 * como ligação daria um clique que não faz nada, que é pior do que
 * texto que se copia.
 */
export function ligacaoAbrivel(localizacao: string | null): string | null {
  if (!localizacao) return null
  let u: URL
  try {
    u = new URL(localizacao)
  } catch {
    return null
  }
  return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null
}

/** Duas referências ao mesmo documento, no mesmo elemento, são uma. */
function chave(d: Documento): string {
  return [d.identificacao, d.nome, d.localizacao, d.revisao].join('\u0000')
}

export function semRepetidos(docs: readonly Documento[]): Documento[] {
  const vistos = new Set<string>()
  const fora: Documento[] = []
  for (const d of docs) {
    const k = chave(d)
    if (vistos.has(k)) continue
    vistos.add(k)
    fora.push(d)
  }
  return fora
}

// --- leitura do modelo -------------------------------------------
//
// A partir daqui já se toca no web-ifc. O que está acima é puro e tem
// testes; isto é a parte que só se pode provar com um IFC à frente.

/** Depois do `resolveTypes`, o `type` de cada objecto já é o nome da
 *  classe em maiúsculas. */
function tipoDe(o: unknown): string {
  if (!o || typeof o !== 'object') return ''
  const t = (o as { type?: unknown }).type
  return typeof t === 'string' ? t.toUpperCase() : ''
}

function referenciaExpressID(ref: unknown): number | null {
  if (typeof ref === 'number') return ref
  if (ref && typeof ref === 'object') {
    const o = ref as { value?: unknown; expressID?: unknown }
    const c = o.expressID ?? o.value
    if (typeof c === 'number') return c
  }
  return null
}

/** Resolve o que estiver do outro lado: já lá está o objecto, ou é uma
 *  referência que ainda é preciso ir buscar. */
function objecto(
  modelId: string,
  v: unknown,
): Record<string, unknown> | null {
  if (v && typeof v === 'object' && tipoDe(v)) {
    return v as Record<string, unknown>
  }
  const id = referenciaExpressID(v)
  if (id == null) return null
  const e = rawIfc.getEntity(modelId, id)
  return e && typeof e === 'object' ? (e as Record<string, unknown>) : null
}

function documentoDe(
  modelId: string,
  alvo: Record<string, unknown>,
): Documento | null {
  const tipo = tipoDe(alvo)
  if (tipo === 'IFCDOCUMENTREFERENCE') {
    const referencia = lerReferencia(alvo)
    const apontado = objecto(modelId, alvo.ReferencedDocument)
    const informacao =
      apontado && tipoDe(apontado) === 'IFCDOCUMENTINFORMATION'
        ? lerInformacao(apontado)
        : null
    return juntar(referencia, informacao)
  }
  if (tipo === 'IFCDOCUMENTINFORMATION') return lerInformacao(alvo)
  // Um tipo que não esperávamos: tenta-se ler como referência, que é a
  // forma mais pobre, em vez de deitar fora o que o modelo trouxe.
  const tentativa = lerReferencia(alvo)
  return temConteudo(tentativa) ? tentativa : null
}

/**
 * Índice expressID do elemento -> documentos que lhe estão associados.
 *
 * Uma passagem por `IfcRelAssociatesDocument`, que é como as outras
 * relações já são lidas neste ficheiro. Uma relação aponta para vários
 * objectos ao mesmo tempo, por isso isto é barato: há muito menos
 * relações do que elementos.
 *
 * Os documentos associados ao TYPE do elemento entram também, marcados
 * com o nome do type (`herdarDoTipo`): uma passagem pelas
 * IfcRelDefinesByType, que também são poucas.
 */
export function documentosDoModelo(
  modelId: string,
): Map<number, Documento[]> {
  const indice = new Map<number, Documento[]>()
  let relIds: number[]
  try {
    relIds = rawIfc.getLineIDsByType(modelId, 'IFCRELASSOCIATESDOCUMENT')
  } catch {
    return indice
  }
  for (const relId of relIds) {
    const rel = rawIfc.getEntity(modelId, relId)
    if (!rel || typeof rel !== 'object') continue
    const r = rel as Record<string, unknown>
    const alvo = objecto(modelId, r.RelatingDocument)
    if (!alvo) continue
    const doc = documentoDe(modelId, alvo)
    if (!doc || !temConteudo(doc)) continue
    const relacionados = r.RelatedObjects
    if (!Array.isArray(relacionados)) continue
    for (const ro of relacionados) {
      const eid = referenciaExpressID(ro)
      if (eid == null) continue
      const lista = indice.get(eid)
      if (lista) lista.push(doc)
      else indice.set(eid, [doc])
    }
  }
  for (const [eid, lista] of indice) indice.set(eid, semRepetidos(lista))
  return herdarDoTipo(indice, relacoesDeTipo(modelId))
}

/** As IfcRelDefinesByType do modelo, reduzidas a ids. */
function relacoesDeTipo(modelId: string): RelacaoDeTipo[] {
  let relIds: number[]
  try {
    relIds = rawIfc.getLineIDsByType(modelId, 'IFCRELDEFINESBYTYPE')
  } catch {
    return []
  }
  const fora: RelacaoDeTipo[] = []
  for (const relId of relIds) {
    const rel = rawIfc.getEntity(modelId, relId)
    if (!rel || typeof rel !== 'object') continue
    const r = rel as Record<string, unknown>
    const tipo = referenciaExpressID(r.RelatingType)
    if (tipo == null || !Array.isArray(r.RelatedObjects)) continue
    const alvo = objecto(modelId, r.RelatingType)
    fora.push({
      tipo,
      nomeDoTipo: alvo ? texto(alvo.Name) : null,
      objectos: r.RelatedObjects.map(referenciaExpressID).filter((n): n is number => n != null),
    })
  }
  return fora
}

/** Índices já construídos, por modelo.
 *
 *  A varredura é uma passagem pelas relações, barata, mas o painel de
 *  propriedades pede isto a cada elemento que se selecciona, e aí
 *  passava a ser uma varredura por clique. O `modelId` é um uuid por
 *  carregamento, por isso uma entrada nunca fica errada: no máximo
 *  fica a ocupar espaço depois de o modelo ser fechado, e é por isso
 *  que só se guardam os últimos quatro. */
const cache = new Map<string, Map<number, Documento[]>>()
const MAXIMO_EM_CACHE = 4

export function documentosDoElemento(
  modelId: string,
  expressID: number,
): Documento[] {
  let indice = cache.get(modelId)
  if (!indice) {
    indice = documentosDoModelo(modelId)
    if (cache.size >= MAXIMO_EM_CACHE) {
      const maisAntigo = cache.keys().next().value
      if (maisAntigo !== undefined) cache.delete(maisAntigo)
    }
    cache.set(modelId, indice)
  }
  return indice.get(expressID) ?? []
}

/** Esquecer o que se sabe de um modelo. Chamado quando ele é fechado. */
export function esquecerDocumentos(modelId: string): void {
  cache.delete(modelId)
}
